/**
 * upsell-products.ts
 *
 * Code-managed upsell products and add-on services.
 * These are merged on top of sheet data in getServices() so they show up
 * in the booking intake form without requiring sheet edits.
 *
 * To add a new upsell:
 *   1. Add a ServiceEntry to UPSELL_PRODUCTS below.
 *   2. Add its id to the relevant service(s) in UPSELL_ID_PATCHES.
 *   3. Done — no sheet changes needed.
 *
 * If you later move a product into the Google Sheet, remove it from here
 * (match by id) so it doesn't appear twice.
 */

import type { ServiceEntry } from '../types/services';

const now = new Date().toISOString();

const upsell = (partial: Partial<ServiceEntry> & Pick<ServiceEntry, 'id' | 'repairType' | 'deviceCategory' | 'basePrice'>): ServiceEntry => ({
  sku:            partial.id,
  status:         'active',
  deviceModels:   [],
  repairCategory: 'repair',
  rushPrice:      null,
  priceTiers:     [],
  turnaround:     '',
  rushTurnaround: '',
  warranty:       '',
  mailInAvailable: true,
  internalNotes:  '',
  disclaimer:     '',
  tags:           [],
  imageUrl:       '',
  imageAlt:       '',
  whatToBring:    '',
  upsellIds:      [],
  isUpsell:       true,
  createdAt:      now,
  updatedAt:      now,
  description:    '',
  ...partial,
});

// ── PS5 & Xbox accessories ────────────────────────────────────────────────────

export const UPSELL_PRODUCTS: ServiceEntry[] = [

  upsell({
    id:           'upsell_hdmi_cable_6ft',
    repairType:   'HDMI Cable 6ft',
    deviceCategory: 'ps5',
    basePrice:    20,
    description:  'High-speed HDMI 2.1 cable, 6 feet. Supports 4K 120Hz. Great to replace if your current cable is damaged or low quality.',
    tags:         ['hdmi', 'cable', 'accessory'],
  }),

  // TODO: migrate accessory catalog/pricing to Google master sheet
  upsell({
    id:           'upsell_hdmi_cable_10ft',
    repairType:   '10-Foot HDMI Cable',
    deviceCategory: 'ps5',
    basePrice:    24,
    description:  'Extra-length HDMI cable for connecting your console to your TV or monitor.',
    tags:         ['hdmi', 'cable', 'accessory'],
  }),

  upsell({
    id:           'upsell_mag_hdmi_breakaway',
    repairType:   'Magnetic HDMI Breakaway Adapter',
    deviceCategory: 'ps5',
    basePrice:    20,
    description:  'Protects your HDMI port from accidental yanks — the cable snaps off magnetically before any damage occurs. Highly recommended after an HDMI port repair.',
    tags:         ['hdmi', 'magnetic', 'breakaway', 'adapter', 'accessory'],
  }),

  upsell({
    id:           'upsell_ps5_cleaning_addon',
    repairType:   'PS5 Deep Clean Add-On',
    deviceCategory: 'ps5',
    basePrice:    30,
    description:  'Full internal cleaning while your PS5 is already open — removes dust buildup from fans, heatsink, and vents. Extends console life and reduces overheating.',
    tags:         ['cleaning', 'ps5', 'dust', 'maintenance'],
  }),

  upsell({
    id:           'upsell_xbox_cleaning_addon',
    repairType:   'Xbox Deep Clean Add-On',
    deviceCategory: 'xbox',
    basePrice:    30,
    description:  'Full internal cleaning while your Xbox is already open — removes dust from fans, heatsink, and vents. Extends console life and prevents overheating.',
    tags:         ['cleaning', 'xbox', 'dust', 'maintenance'],
  }),

  upsell({
    id:           'upsell_cleaning_plan',
    repairType:   'Annual Cleaning Plan',
    deviceCategory: 'ps5',
    basePrice:    75,
    description:  '2 professional cleanings within 12 months at a discounted rate. Available after your first cleaning with us — a great way to keep your console running cool year-round.',
    disclaimer:   'Requires that an initial cleaning has already been performed by GameFix Solutions.',
    tags:         ['cleaning', 'plan', 'maintenance', 'annual'],
  }),

  // ── Custom PS5 controller upsell ──────────────────────────────────────────

  upsell({
    id:             'upsell_custom_controller',
    repairType:     'Custom PS5 Controller Build',
    deviceCategory: 'controller',
    basePrice:      150,
    description:    'Starting at $150 for a shell swap or $190 for a full graphic build — all builds include TMR hall-effect joysticks (no drift). Choose from dozens of designs, or bring your own vision. Ask us about add-ons: mouse-click triggers, back buttons, rumble delete, and more.',
    tags:           ['custom', 'controller', 'ps5', 'tmr', 'build'],
  }),

];

// ── Upsell ID patches ─────────────────────────────────────────────────────────
// Maps existing sheet service IDs → additional upsell IDs to inject.
// These are MERGED with whatever upsell_ids the sheet already defines.
//
// Format:  'sheet_service_id': ['upsell_id_1', 'upsell_id_2', ...]

export const UPSELL_ID_PATCHES: Record<string, string[]> = {

  // ── PS5 HDMI repairs ──────────────────────────────────────────────────────
  ps5_hdmi:      ['upsell_hdmi_cable_6ft', 'upsell_hdmi_cable_10ft', 'upsell_mag_hdmi_breakaway', 'upsell_ps5_cleaning_addon', 'upsell_custom_controller'],
  ps5_slim_hdmi: ['upsell_hdmi_cable_6ft', 'upsell_hdmi_cable_10ft', 'upsell_mag_hdmi_breakaway', 'upsell_ps5_cleaning_addon', 'upsell_custom_controller'],
  ps5_pro_hdmi:  ['upsell_hdmi_cable_6ft', 'upsell_hdmi_cable_10ft', 'upsell_mag_hdmi_breakaway', 'upsell_ps5_cleaning_addon', 'upsell_custom_controller'],

  // ── PS5 no power ──────────────────────────────────────────────────────────
  ps5_no_power_diagnostic: ['upsell_ps5_cleaning_addon', 'upsell_custom_controller'],

  // ── PS5 cleaning / overheating ────────────────────────────────────────────
  ps5_overheating_and_cleaning:     ['upsell_cleaning_plan', 'upsell_custom_controller'],
  ps5_pro_overheating_and_cleaning: ['upsell_cleaning_plan', 'upsell_custom_controller'],

  // ── PS5 other console repairs → custom controller ─────────────────────────
  ps5_ripped_fan_connector:          ['upsell_custom_controller'],
  ps5_disc_drive_replacement:        ['upsell_custom_controller'],
  ps5_power_supply_replacement:      ['upsell_custom_controller'],
  ps5_pro_power_supply_replacement:  ['upsell_custom_controller'],
  ps5_software_issue_wont_update:    ['upsell_custom_controller'],
  ps5_usb_ethernet_port_replacement: ['upsell_custom_controller'],
  ps5_fan_replacement:               ['upsell_custom_controller'],
  ps5_usb_c_port:                    ['upsell_custom_controller'],
  ps5_battery_replacement:           ['upsell_custom_controller'],

  // ── Xbox HDMI repairs → full accessory set ────────────────────────────────
  xbox_series_x_hdmi_port_repair: ['upsell_hdmi_cable_6ft', 'upsell_hdmi_cable_10ft', 'upsell_mag_hdmi_breakaway', 'upsell_xbox_cleaning_addon', 'upsell_cleaning_plan'],
  xbox_series_s_hdmi_port_repair: ['upsell_hdmi_cable_6ft', 'upsell_hdmi_cable_10ft', 'upsell_mag_hdmi_breakaway', 'upsell_xbox_cleaning_addon', 'upsell_cleaning_plan'],

  // ── Xbox no power → full accessory set ───────────────────────────────────
  xbox_series_s_x_no_power_diagnostic: ['upsell_hdmi_cable_6ft', 'upsell_hdmi_cable_10ft', 'upsell_mag_hdmi_breakaway', 'upsell_xbox_cleaning_addon', 'upsell_cleaning_plan'],

  // ── Xbox cleaning / overheating → full accessory set ─────────────────────
  xbox_series_x_overheating_and_cleaning: ['upsell_hdmi_cable_6ft', 'upsell_hdmi_cable_10ft', 'upsell_mag_hdmi_breakaway', 'upsell_xbox_cleaning_addon', 'upsell_cleaning_plan'],
  xbox_series_s_overheating_and_cleaning: ['upsell_hdmi_cable_6ft', 'upsell_hdmi_cable_10ft', 'upsell_mag_hdmi_breakaway', 'upsell_xbox_cleaning_addon', 'upsell_cleaning_plan'],

  // ── Xbox other console repairs → full accessory set ──────────────────────
  xbox_series_x_disc_drive_replacement: ['upsell_hdmi_cable_6ft', 'upsell_hdmi_cable_10ft', 'upsell_mag_hdmi_breakaway', 'upsell_xbox_cleaning_addon', 'upsell_cleaning_plan'],
  xbox_software_issue_diagnostic:       ['upsell_xbox_cleaning_addon', 'upsell_cleaning_plan'],
  xbox_usb_ethernet_port_replacement:   ['upsell_xbox_cleaning_addon', 'upsell_cleaning_plan'],
  xbox_series_x_fan_replacement:        ['upsell_hdmi_cable_6ft', 'upsell_hdmi_cable_10ft', 'upsell_mag_hdmi_breakaway', 'upsell_xbox_cleaning_addon', 'upsell_cleaning_plan'],
  xbox_series_s_fan_replacement:        ['upsell_hdmi_cable_6ft', 'upsell_hdmi_cable_10ft', 'upsell_mag_hdmi_breakaway', 'upsell_xbox_cleaning_addon', 'upsell_cleaning_plan'],

};
